import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { usePageTitle } from "@/hooks/usePageTitle";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/useAuth";
import AdminLayout from "@/components/layout/admin-layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { Radio, Upload, Trash2, FileText, Plus, CheckCircle, Loader2, AlertCircle, Globe, FileSpreadsheet } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import type { Station, File as FileType } from "@shared/schema";
import TapscanProcessor from "@/components/admin/tapscan-processor";
import ProfileQueue from "@/components/admin/profile-queue";
import VBREngineManager from "@/components/admin/vbr-engine-manager";
import CopyApiManager from "@/components/admin/copy-api-manager";
import ExperienceManager from "@/components/admin/experience-manager";
import CopyApiTester from "@/components/admin/copy-api-tester";
import { UserManagement } from "@/components/admin/user-management";
import { ApiStatus } from "@/components/admin/api-status";
import GenericProcessor from "@/components/admin/generic-processor";
import {
  ADMIN_SECTIONS,
  DEFAULT_ADMIN_SECTION,
  findSectionBySlug,
  sectionHref,
} from "@/lib/admin-sections";

export default function AdminSettings() {
  usePageTitle("Admin Settings");
  const [location, navigate] = useLocation();
  const { toast } = useToast();
  const { user } = useAuth();
  
  const [createStationOpen, setCreateStationOpen] = useState(false);
  const [uploadGlobalFilesOpen, setUploadGlobalFilesOpen] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);
  const [citationTexts, setCitationTexts] = useState<{ [key: string]: string }>({});
  const [newStationName, setNewStationName] = useState("");
  const [newStationDescription, setNewStationDescription] = useState("");

  // Fetch all stations
  const { data: stations = [], isLoading: stationsLoading, refetch: refetchStations } = useQuery<Station[]>({
    queryKey: ["/api/stations"],
  });

  // Fetch global files
  const { data: globalFiles = [], isLoading: filesLoading, refetch: refetchGlobalFiles } = useQuery<FileType[]>({
    queryKey: ["/api/global/files"],
    refetchInterval: 30000, // Refresh every 30 seconds instead of 5
  });

  // Create station mutation
  const createStation = useMutation({
    mutationFn: async (data: { name: string; description: string }) => {
      return await apiRequest("/api/stations", "POST", data);
    },
    onSuccess: (data) => {
      toast({
        title: "Station created",
        description: "Your new station is ready to use.",
      });
      setCreateStationOpen(false);
      setNewStationName("");
      setNewStationDescription("");
      refetchStations();
      // Navigate to the new station VBR engine
      navigate(`/stations/${data.id}/vbr`);
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to create station",
        variant: "destructive",
      });
    },
  });

  // Upload global files mutation
  const uploadGlobalFiles = useMutation({
    mutationFn: async () => {
      if (!selectedFiles) return;
      
      const formData = new FormData();
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        formData.append("files", file);
        // Add citation text for this file
        const citationText = citationTexts[file.name] || file.name;
        formData.append(`citationText_${i}`, citationText);
      }
      
      const response = await fetch(`/api/global/files`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Upload failed");
      }
      
      return response.json();
    },
    onSuccess: () => {
      toast({
        title: "Files uploaded",
        description: "Global knowledge base files are being processed.",
      });
      setUploadGlobalFilesOpen(false);
      setSelectedFiles(null);
      setCitationTexts({});
      refetchGlobalFiles();
    },
    onError: (error: Error) => {
      toast({
        title: "Upload failed",
        description: error.message || "Failed to upload files",
        variant: "destructive",
      });
    },
  });

  // Delete global file mutation
  const deleteGlobalFile = useMutation({
    mutationFn: async (fileId: string) => {
      await apiRequest(`/api/global/files/${fileId}`, "DELETE");
    },
    onSuccess: () => {
      toast({
        title: "File deleted",
        description: "The global file has been removed.",
      });
      refetchGlobalFiles();
    },
    onError: (error: Error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to delete file",
        variant: "destructive",
      });
    },
  });

  const handleCreateStation = () => {
    if (newStationName.trim()) {
      createStation.mutate({
        name: newStationName.trim(),
        description: newStationDescription.trim(),
      });
    }
  };

  const handleUploadGlobalFiles = () => {
    if (selectedFiles && selectedFiles.length > 0) {
      uploadGlobalFiles.mutate();
    }
  };

  // Section is driven by the URL: /admin/<slug>. Unknown slug → redirect to
  // the default landing section so the URL stays in sync with what's rendered.
  const slug = location.match(/^\/admin\/([^\/?#]+)/)?.[1] ?? DEFAULT_ADMIN_SECTION.slug;
  const activeSection = findSectionBySlug(slug);

  useEffect(() => {
    if (!activeSection) {
      navigate(sectionHref(DEFAULT_ADMIN_SECTION));
    }
  }, [activeSection, navigate]);

  if (!activeSection) {
    return null;
  }

  return (
    <AdminLayout>
      {/* Per-section header */}
      <div className="mb-8 flex items-start gap-3">
        <div className="p-3 bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shrink-0">
          <activeSection.icon className="h-6 w-6 text-white" />
        </div>
        <div>
          <h1 className="text-3xl font-bold">{activeSection.label}</h1>
          {activeSection.description && (
            <p className="text-gray-600">{activeSection.description}</p>
          )}
        </div>
      </div>

      {/* Active section content. Each branch below corresponds to one row in
          ADMIN_SECTIONS. The `ratings` and `usage` sections are not handled
          here — they live at their own page routes and use AdminLayout
          directly. */}
      <div className="space-y-6">
        {activeSection.slug === "stations" && (
          <>
          <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle>Station Management</CardTitle>
                <CardDescription>
                  Create and manage knowledge stations
                </CardDescription>
              </div>
              <Dialog 
                open={createStationOpen} 
                onOpenChange={(open) => {
                  setCreateStationOpen(open);
                  // Clear form when dialog closes
                  if (!open) {
                    setNewStationName("");
                    setNewStationDescription("");
                  }
                }}
              >
                <DialogTrigger asChild>
                  <Button data-testid="button-new-station">
                    <Plus className="h-4 w-4 mr-2" />
                    New Station
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Create New Station</DialogTitle>
                    <DialogDescription>
                      A station is a dedicated knowledge base for specific topics or clients
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label htmlFor="station-name">Station Name</Label>
                      <Input
                        id="station-name"
                        placeholder="e.g., Sales Team Resources"
                        value={newStationName}
                        onChange={(e) => setNewStationName(e.target.value)}
                        data-testid="input-station-name"
                      />
                    </div>
                    <div>
                      <Label htmlFor="station-description">Description (Optional)</Label>
                      <Textarea
                        id="station-description"
                        placeholder="Describe what this station is for..."
                        rows={3}
                        value={newStationDescription}
                        onChange={(e) => setNewStationDescription(e.target.value)}
                        data-testid="input-station-description"
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button
                      variant="outline"
                      onClick={() => setCreateStationOpen(false)}
                      data-testid="button-cancel-create"
                    >
                      Cancel
                    </Button>
                    <Button
                      onClick={handleCreateStation}
                      disabled={createStation.isPending || !newStationName.trim()}
                      data-testid="button-confirm-create"
                    >
                      {createStation.isPending ? "Creating..." : "Create Station"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            {stationsLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
            ) : stations.length === 0 ? (
              <div className="text-center py-8">
                <Radio className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No stations created yet</p>
                <p className="text-sm text-gray-400">Create your first station to get started</p>
              </div>
            ) : (
              <div className="space-y-2">
                {stations.map((station: Station) => (
                  <div
                    key={station.id}
                    className="flex items-center justify-between p-3 border rounded-lg"
                    data-testid={`station-item-${station.id}`}
                  >
                    <div className="flex items-center space-x-3">
                      <Radio className="h-5 w-5 text-gray-400" />
                      <div>
                        <p className="font-medium">{station.name}</p>
                        <p className="text-sm text-gray-500">
                          {station.description || "No description"} • 
                          Created {formatDistanceToNow(new Date(station.createdAt!), { addSuffix: true })}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        navigate(`/stations/${station.id}/settings?from=admin`);
                      }}
                      data-testid={`button-manage-station-${station.id}`}
                    >
                      Manage
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Global Knowledge Base */}
        <Card>
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <Globe className="h-5 w-5 text-blue-600" />
                  <CardTitle>Global Knowledge Base</CardTitle>
                </div>
                <CardDescription>
                  Documents that apply to all stations (e.g., company policies, general industry knowledge)
                </CardDescription>
              </div>
              <Dialog open={uploadGlobalFilesOpen} onOpenChange={setUploadGlobalFilesOpen}>
                <DialogTrigger asChild>
                  <Button size="sm" data-testid="button-upload-global-files">
                    <Upload className="h-4 w-4 mr-2" />
                    Upload Global Files
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Upload Global Knowledge Base Files</DialogTitle>
                    <DialogDescription>
                      These files will be available to all stations. Supported formats: PDF, Word, Excel, PowerPoint, CSV, Text, Markdown
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Input
                        type="file"
                        multiple
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.csv,.txt,.md"
                        onChange={(e) => {
                          setSelectedFiles(e.target.files);
                          // Initialize citation texts for new files
                          if (e.target.files) {
                            const newCitationTexts: { [key: string]: string } = {};
                            for (let i = 0; i < e.target.files.length; i++) {
                              const file = e.target.files[i];
                              newCitationTexts[file.name] = citationTexts[file.name] || "";
                            }
                            setCitationTexts(newCitationTexts);
                          }
                        }}
                        data-testid="input-global-file-upload"
                      />
                    </div>
                    {selectedFiles && selectedFiles.length > 0 && (
                      <div className="space-y-3">
                        <p className="text-sm font-medium">Citation Text for Each File:</p>
                        {Array.from(selectedFiles).map((file, index) => (
                          <div key={index} className="space-y-2 p-3 border rounded-lg">
                            <div className="flex items-center gap-2">
                              <FileText className="h-4 w-4 text-gray-500" />
                              <span className="text-sm font-medium">{file.name}</span>
                              <Badge variant="outline" className="text-xs">
                                {Math.round(file.size / 1024)} KB
                              </Badge>
                            </div>
                            <Input
                              placeholder="e.g., Industry Best Practices Guide 2024"
                              value={citationTexts[file.name] || ""}
                              onChange={(e) => {
                                setCitationTexts({
                                  ...citationTexts,
                                  [file.name]: e.target.value
                                });
                              }}
                              className="text-sm"
                              data-testid={`input-global-citation-${index}`}
                            />
                            <p className="text-xs text-gray-500">
                              This text will be used when citing this document in VBRs across all stations
                            </p>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="flex justify-end space-x-2">
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          setUploadGlobalFilesOpen(false);
                          setSelectedFiles(null);
                        }}
                        data-testid="button-cancel-global-upload"
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleUploadGlobalFiles}
                        disabled={!selectedFiles || selectedFiles.length === 0 || uploadGlobalFiles.isPending}
                        data-testid="button-confirm-global-upload"
                      >
                        {uploadGlobalFiles.isPending ? "Uploading..." : "Upload Files"}
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            {filesLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-12 w-full" />
                <Skeleton className="h-12 w-full" />
              </div>
            ) : globalFiles.length === 0 ? (
              <div className="text-center py-8">
                <Globe className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500">No global files uploaded yet</p>
                <p className="text-sm text-gray-400">Upload files that apply to all stations</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {globalFiles.map((file: any) => (
                  <div
                    key={file.id}
                    className="flex items-center justify-between p-3 border rounded-lg"
                    data-testid={`global-file-item-${file.id}`}
                  >
                    <div className="flex items-center space-x-3">
                      <FileText className="h-5 w-5 text-gray-400" />
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-medium" data-testid={`text-global-filename-${file.id}`}>
                            {file.originalName}
                          </p>
                          {/* Processing status indicator */}
                          {file.metadata?.processingStatus === 'processing' && (
                            <div className="flex items-center gap-1 text-yellow-600">
                              <Loader2 className="h-4 w-4 animate-spin" />
                              <span className="text-xs">Processing embeddings...</span>
                            </div>
                          )}
                          {file.metadata?.processingStatus === 'completed' && (
                            <div className="flex items-center gap-1 text-green-600">
                              <CheckCircle className="h-4 w-4" />
                              <span className="text-xs">Ready</span>
                            </div>
                          )}
                          {file.metadata?.processingStatus === 'failed' && (
                            <div className="flex items-center gap-1 text-red-600">
                              <AlertCircle className="h-4 w-4" />
                              <span className="text-xs">Processing failed</span>
                            </div>
                          )}
                        </div>
                        <p className="text-sm text-gray-500">
                          {file.fileSize < 1024 * 1024 
                            ? `${Math.round(file.fileSize / 1024)} KB`
                            : `${(file.fileSize / (1024 * 1024)).toFixed(1)} MB`} • 
                          Facts: {(file.metadata?.factCount || 0).toLocaleString()} • 
                          {formatDistanceToNow(new Date(file.createdAt), { addSuffix: true })}
                        </p>
                        {file.citationText && (
                          <p className="text-xs text-gray-600 italic mt-1">
                            Citation: {file.citationText}
                          </p>
                        )}
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteGlobalFile.mutate(file.id)}
                      disabled={deleteGlobalFile.isPending}
                      data-testid={`button-delete-global-file-${file.id}`}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
          </>
        )}

        {activeSection.slug === "users" && <UserManagement />}
        {activeSection.slug === "vbr" && <VBREngineManager />}
        {activeSection.slug === "copy-api" && <CopyApiManager />}
        {activeSection.slug === "copy-api-test" && <CopyApiTester />}
        {activeSection.slug === "experience" && <ExperienceManager />}
        {activeSection.slug === "npr" && <ProfileQueue />}
        {activeSection.slug === "tapscan" && <TapscanProcessor />}
        {activeSection.slug === "generic" && <GenericProcessor />}
        {activeSection.slug === "api-status" && <ApiStatus />}
      </div>
    </AdminLayout>
  );
}